import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  groupArchivesByMatchId,
  mapLiveDashboardRowToLeagueGame,
  resolveLiveStreamKey,
} from '../../api';
import { isArchivePlaybackPlayable } from '../../archivePlayback';
import { resolveTeamVisual } from '../../cfbBranding/teamVisualResolver';
import { parseStageSortKey } from '../../leagueScheduleSort';
import { broadcasterDisplayName } from '../../stream/broadcasterName';
import { formatReplayDuration } from '../../stream/replayDuration';
import { carryStreamSearch, isMemberPreview } from '../../stream/streamPages';
import {
  finalScore,
  isBeforeUpcomingFloor,
  matchIsOnLiveSlate,
  upcomingStageFloor,
} from '../../stream/streamGamesModel';
import type { LscArchiveItem, LscLeague, LscLiveDashboardRow, LscMatch, LscMember } from '../../types';
import { lscPath } from '../../routing';
import { groupLiveRowsByMatch, type GroupedLiveGame } from '../../stream/groupLiveRowsByMatch';
import { vodExpiryLabel } from '../../stream/vodExpiryLabel';
import { Play } from 'lucide-react';
import LeagueHomeReplayPlayer from '../leagueHome/LeagueHomeReplayPlayer';
import { deriveUpcomingMatches } from '../leagueHome/leagueHomeUtils';
import LiveGameEmbedPanel from '../LiveGameEmbedPanel';
import MyClaimOpportunitiesPanel from '../MyClaimOpportunitiesPanel';
import StreamOperatorStrip from './StreamOperatorStrip';
import { StreamSurfaceChrome } from './StreamSurfaceChrome';
import MatchupTile, { type MatchupTileSide } from './MatchupTile';
import Rail from './Rail';
import StatusPill from './StatusPill';
import TeamCircle from './TeamCircle';

export const STREAM_HOME_FOOTER =
  'Replays play from Twitch or YouTube. Twitch removes past broadcasts after 7 to 60 days; YouTube replays do not expire.';

/** 49ers start with a digit and would sort first in a default string compare. Pin them last. */
export function isFortyNinersBrowseName(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n === '49ers' || n.endsWith(' 49ers');
}

/** Case-insensitive displayed-name sort. ASCII lowercasing — not localeCompare. */
export function compareBrowseTeamDisplayNames(left: string, right: string): number {
  const aLast = isFortyNinersBrowseName(left);
  const bLast = isFortyNinersBrowseName(right);
  if (aLast !== bLast) return aLast ? 1 : -1;
  const a = left.trim().toLowerCase();
  const b = right.trim().toLowerCase();
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

const ELAPSED_CAP_MINUTES = 55;
const ELAPSED_BAR_CAP = 0.95;

export type StreamLeagueHomeViewProps = {
  leagueId: string;
  league: LscLeague;
  isCommissioner: boolean;
  isOperator: boolean;
  isOwner: boolean;
  liveRows: LscLiveDashboardRow[];
  suppressArchiveFallback: boolean;
  archives: LscArchiveItem[];
  matches: LscMatch[];
  members?: LscMember[];
  now?: Date;
  selectedLiveStreamKey?: string | null;
  onSelectLive?: (liveStreamKey: string) => void;
  selectedArchive?: LscArchiveItem | null;
  onSelectArchive?: (archive: LscArchiveItem) => void;
  loadFailed?: boolean;
};

function sideWord(side: string | null | undefined): string {
  const value = (side ?? '').trim().toLowerCase();
  if (value === 'away') return 'Away';
  if (value === 'home') return 'Home';
  return 'Broadcast';
}

function watchLabel(row: LscLiveDashboardRow, members: LscMember[]): string {
  const name =
    broadcasterDisplayName({
      login: row.twitch_login,
      memberName: row.member_name,
      twitchDisplayName: row.display_name,
      members,
    }) || 'broadcaster';
  return `Watch ${name} · ${sideWord(row.broadcaster_side)}`;
}

function elapsedProgress(minutes: number | null): number | null {
  if (minutes == null) return null;
  return Math.min(ELAPSED_BAR_CAP, Math.max(0, minutes / ELAPSED_CAP_MINUTES));
}

function tileSide(label: string | null | undefined, gameKey?: string | null): MatchupTileSide {
  const name = label?.trim() || 'Team';
  const visual = resolveTeamVisual({ label: name, gameKey: gameKey || undefined });
  return {
    name: visual.displayName,
    abbreviation: visual.abbreviation,
    tileColors: visual.tileColors,
  };
}

const PRIMARY_BUTTON =
  'inline-flex min-h-12 w-fit items-center gap-2 rounded-stream-md bg-stream-ink px-5 font-stream-display text-sm font-bold uppercase tracking-wide text-stream-ground';
const SECONDARY_BUTTON =
  'inline-flex min-h-12 w-fit items-center gap-2 rounded-stream-md border-2 border-stream-ink bg-transparent px-5 font-stream-display text-sm font-bold uppercase tracking-wide text-stream-ink';

function broadcastCountLabel(count: number): string | null {
  if (count <= 1) return null;
  return `${count} broadcasts`;
}

function liveMeta(game: GroupedLiveGame): string {
  const week = game.lead.row.stage_label?.trim() || game.lead.row.week_label?.trim() || 'Live';
  const parts = [week];
  if (game.lead.elapsedMinutes != null) {
    parts.push(`Live for ${game.lead.elapsedMinutes} min`);
  }
  const broadcasts = broadcastCountLabel(game.sources.length);
  if (broadcasts) parts.push(broadcasts);
  return parts.join(' · ');
}

function replayMeta(archive: LscArchiveItem): string {
  const parts = [
    archive.match_summary?.stage_label?.trim() || null,
    formatReplayDuration(archive.duration_seconds),
    broadcastCountLabel(archive.sources?.length ?? 1),
  ];
  return parts.filter(Boolean).join(' · ');
}

function watchMatchLabel(away?: string | null, home?: string | null): string {
  return `Watch ${away?.trim() || 'Away'} at ${home?.trim() || 'Home'}`;
}

function playedAt(archive: LscArchiveItem): number {
  const parsed = Date.parse(archive.vod_created_at ?? archive.published_at ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

function newestReplay(archives: LscArchiveItem[]): LscArchiveItem | null {
  const playable = archives.filter(isArchivePlaybackPlayable);
  const pool = playable.length > 0 ? playable : archives;
  return [...pool].sort((left, right) => playedAt(right) - playedAt(left))[0] ?? null;
}

function matchupTitle(away: string | null | undefined, home: string | null | undefined): string {
  return `${away?.trim() || 'Away'} @ ${home?.trim() || 'Home'}`;
}

function broadcasterCaption(
  sources: Array<{ name?: string | null; login?: string | null; side?: string | null }>
): string | null {
  const parts = sources
    .map((source) => {
      const login = source.name?.trim() || source.login?.trim();
      if (!login) return null;
      const side = (source.side ?? '').trim().toLowerCase();
      if (sources.length === 1 && side) return `${login} (${side})`;
      return login;
    })
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : null;
}

function TileCaption({
  kicker,
  title,
  detail,
}: {
  kicker?: string | null;
  title: string;
  detail?: string | null;
}) {
  return (
    <div className="mt-2 flex flex-col gap-0.5" data-testid="stream-tile-caption">
      {kicker ? <p className="text-xs text-stream-muted">{kicker}</p> : null}
      <p className="font-stream-display text-lg font-bold uppercase leading-tight">{title}</p>
      {detail ? <p className="text-sm text-stream-muted">{detail}</p> : null}
    </div>
  );
}

const StreamLeagueHomeView: React.FC<StreamLeagueHomeViewProps> = ({
  leagueId,
  league,
  isCommissioner,
  isOperator,
  isOwner,
  liveRows,
  suppressArchiveFallback,
  archives,
  matches,
  members = [],
  now = new Date(),
  selectedLiveStreamKey = null,
  onSelectLive,
  selectedArchive = null,
  onSelectArchive,
  loadFailed = false,
}) => {
  const [params] = useSearchParams();
  const previewing = isMemberPreview(params.toString(), { isOwner, isCommissioner, isOperator });
  const operatorView = isOperator && !previewing;
  const streamViewer = { operator: isOwner || isCommissioner || isOperator };
  const streamQuery = carryStreamSearch(params.toString(), streamViewer);
  const games = useMemo(
    () => groupLiveRowsByMatch(liveRows, league.streaming_obligation, now),
    [liveRows, league.streaming_obligation, now]
  );
  const heroGame = games[0] ?? null;
  const replayHero = heroGame || suppressArchiveFallback ? null : newestReplay(archives);
  const otherLive = games.slice(1);
  const archivesByMatchId = useMemo(
    () => groupArchivesByMatchId(archives.flatMap((item) => (item.sources?.length ? item.sources : [item]))),
    [archives]
  );
  const upcoming = useMemo(
    () => deriveUpcomingMatches(matches, archivesByMatchId, league, now),
    [matches, archivesByMatchId, league, now]
  );
  const upcomingFloor = upcomingStageFloor(league.active_stage_label, games);
  const upcomingMatches = [...upcoming.future, ...upcoming.currentSlate].filter((match) => {
    if (matchIsOnLiveSlate(match, games) || finalScore(match)) return false;
    const status = (match.status ?? '').trim().toLowerCase();
    if (status === 'final' || status === 'completed' || status === 'complete') return false;
    if (upcomingFloor != null) {
      const key = parseStageSortKey(match.stage_label);
      if (key < 5000 && key !== upcomingFloor) return false;
    }
    return !isBeforeUpcomingFloor(match.stage_label, upcomingFloor);
  });
  const replayRail = useMemo(
    () => [...archives].sort((left, right) => playedAt(right) - playedAt(left)),
    [archives]
  );
  const teams = useMemo(() => {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const match of matches) {
      for (const label of [match.away_side_label, match.home_side_label]) {
        const name = (label ?? '').trim();
        const key = name.toLowerCase();
        if (!name || seen.has(key)) continue;
        seen.add(key);
        labels.push(name);
      }
    }
    return labels;
  }, [matches]);
  const browseTeams = useMemo(() => {
    const items = teams.map((label) => {
      const visual = resolveTeamVisual({ label, gameKey: league.game_key || undefined });
      return { label, visual };
    });
    // Display-name sort at render. API / matches order is left unchanged.
    return [...items].sort((left, right) =>
      compareBrowseTeamDisplayNames(left.visual.displayName, right.visual.displayName)
    );
  }, [teams, league.game_key]);

  const selectedLive = heroGame
    ? games
        .flatMap((game) => game.sources)
        .find((source) => resolveLiveStreamKey(mapLiveDashboardRowToLeagueGame(source.row)) === selectedLiveStreamKey)
    : undefined;

  const archiveHref = `${lscPath(`/leagues/${leagueId}/archive`)}${streamQuery}`;

  return (
    <div
      className="stream-scope flex flex-col gap-8 overflow-x-hidden bg-stream-ground font-stream-body text-stream-ink"
      data-testid="stream-league-home"
    >
      <StreamSurfaceChrome
        leagueId={leagueId}
        streamPages={league.stream_pages}
        canSet={league.can_set_stream_pages}
        isOperator={isOwner || isCommissioner || isOperator}
      />
      {operatorView ? (
        <StreamOperatorStrip leagueId={leagueId} isCommissioner={isCommissioner} isOwner={isOwner} />
      ) : null}

      {heroGame ? (
        <section data-testid="stream-home-hero" className="flex flex-col gap-6 lg:flex-row lg:items-center">
          <div
            className="order-2 flex w-full flex-col items-start gap-4 lg:order-1 lg:w-[40%]"
            data-testid="stream-home-hero-copy"
          >
            <StatusPill variant="live" label="Live now" />
            <h1 className="font-stream-display text-4xl font-bold uppercase leading-none tracking-wide lg:text-[56px] xl:text-[64px]">
              {matchupTitle(heroGame.lead.row.away_team, heroGame.lead.row.home_team)}
            </h1>
            <p className="text-stream-muted" data-testid="stream-home-live-meta">
              {liveMeta(heroGame)}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {heroGame.sources.map((source, index) => {
                const key = resolveLiveStreamKey(mapLiveDashboardRowToLeagueGame(source.row));
                return (
                  <button
                    key={key}
                    type="button"
                    className={index === 0 ? PRIMARY_BUTTON : SECONDARY_BUTTON}
                    data-testid="stream-watch-broadcast"
                    onClick={() => onSelectLive?.(key)}
                  >
                    {index === 0 ? <Play className="h-4 w-4 fill-current" aria-hidden /> : null}
                    {watchLabel(source.row, members)}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            className="order-1 w-full lg:order-2 lg:w-[60%]"
            data-testid="stream-home-hero-art"
          >
            <div className="mx-auto w-full max-w-[746px]">
              <MatchupTile
                size="hero"
                away={tileSide(heroGame.lead.row.away_team, league.game_key)}
                home={tileSide(heroGame.lead.row.home_team, league.game_key)}
                progress={elapsedProgress(heroGame.lead.elapsedMinutes)}
              />
            </div>
          </div>
          {selectedLive ? (
            <div className="order-3 w-full">
              <LiveGameEmbedPanel game={mapLiveDashboardRowToLeagueGame(selectedLive.row)} />
            </div>
          ) : null}
        </section>
      ) : replayHero ? (
        <section data-testid="stream-home-hero" className="flex flex-col gap-6 lg:flex-row lg:items-center">
          <div
            className="order-2 flex w-full flex-col items-start gap-4 lg:order-1 lg:w-[40%]"
            data-testid="stream-home-hero-copy"
          >
            <StatusPill variant="neutral" label="Newest replay" />
            <h1 className="font-stream-display text-4xl font-bold uppercase leading-none tracking-wide lg:text-[56px] xl:text-[64px]">
              {matchupTitle(
                replayHero.match_summary?.away_side_label,
                replayHero.match_summary?.home_side_label
              )}
            </h1>
            <p className="text-stream-muted" data-testid="stream-home-replay-meta">
              {replayMeta(replayHero)}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={PRIMARY_BUTTON}
                data-testid="stream-watch-replay"
                onClick={() => onSelectArchive?.(replayHero)}
              >
                <Play className="h-4 w-4 fill-current" aria-hidden />
                Watch replay
              </button>
              <Link
                to={`${lscPath(
                  `/leagues/${encodeURIComponent(leagueId)}/archive/${encodeURIComponent(replayHero.id)}`
                )}${streamQuery}`}
                state={{ watchFrom: 'home' }}
                className={SECONDARY_BUTTON}
                data-testid="stream-game-details"
              >
                Game details
              </Link>
            </div>
          </div>
          <div
            className="order-1 w-full lg:order-2 lg:w-[60%]"
            data-testid="stream-home-hero-art"
          >
            <div className="mx-auto w-full max-w-[746px]">
              <MatchupTile
                size="hero"
                away={tileSide(replayHero.match_summary?.away_side_label, league.game_key)}
                home={tileSide(replayHero.match_summary?.home_side_label, league.game_key)}
              />
            </div>
          </div>
          {selectedArchive ? (
            <div className="order-3 w-full">
              <LeagueHomeReplayPlayer leagueId={leagueId} archive={selectedArchive} />
            </div>
          ) : null}
        </section>
      ) : loadFailed ? null : (
        <section data-testid="stream-home-empty" className="flex flex-col gap-2 rounded-stream-lg bg-stream-surface p-6">
          <h1 className="font-stream-display text-3xl font-bold uppercase">
            {suppressArchiveFallback ? 'Live verification in progress' : 'Nothing live right now'}
          </h1>
          <p className="text-stream-muted">
            {suppressArchiveFallback
              ? 'A stream is being checked before it can lead the home.'
              : 'When a game is on, or a replay is published, it will lead this page.'}
          </p>
        </section>
      )}

      {!isCommissioner ? (
        <div data-testid="stream-member-claims">
          <MyClaimOpportunitiesPanel leagueId={leagueId} compactEmpty />
        </div>
      ) : null}

      {otherLive.length > 0 ? (
        <Rail title="Live now" scrollerLabel="Other live games">
          {otherLive.map((game) => (
            <button
              type="button"
              key={game.competitionMatchId ?? game.lead.row.stream_key ?? game.lead.row.twitch_login}
              className="w-72 shrink-0 text-left"
              data-testid="stream-live-tile"
              aria-label={watchMatchLabel(game.lead.row.away_team, game.lead.row.home_team)}
              onClick={() =>
                onSelectLive?.(resolveLiveStreamKey(mapLiveDashboardRowToLeagueGame(game.lead.row)))
              }
            >
              <MatchupTile
                away={tileSide(game.lead.row.away_team, league.game_key)}
                home={tileSide(game.lead.row.home_team, league.game_key)}
                topLeft={<StatusPill variant="live" label="Live" />}
                progress={elapsedProgress(game.lead.elapsedMinutes)}
              />
              <TileCaption
                kicker={liveMeta(game)}
                title={matchupTitle(game.lead.row.away_team, game.lead.row.home_team)}
                detail={                broadcasterCaption(
                  game.sources.map((source) => ({
                    name: broadcasterDisplayName({
                      login: source.row.twitch_login,
                      memberName: source.row.member_name,
                      twitchDisplayName: source.row.display_name,
                      members,
                    }),
                    login: source.row.twitch_login,
                    side: source.row.broadcaster_side,
                  }))
                )}
              />
            </button>
          ))}
        </Rail>
      ) : null}

      {replayRail.length > 0 ? (
        <Rail title="Latest replays" scrollerLabel="Latest replays" viewAllHref={archiveHref}>
          {replayRail.map((archive) => {
            const expiry = vodExpiryLabel(archive.vod_created_at, archive.playback_status, now);
            const duration = formatReplayDuration(archive.duration_seconds);
            const broadcastCount = archive.sources?.length ?? 0;
            return (
              <Link
                key={archive.id}
                to={`${lscPath(
                  `/leagues/${encodeURIComponent(leagueId)}/archive/${encodeURIComponent(archive.id)}`
                )}${streamQuery}`}
                className="block w-72 shrink-0"
                data-testid="stream-replay-tile"
                aria-label={watchMatchLabel(
                  archive.match_summary?.away_side_label,
                  archive.match_summary?.home_side_label
                )}
              >
                <MatchupTile
                  away={tileSide(archive.match_summary?.away_side_label, league.game_key)}
                  home={tileSide(archive.match_summary?.home_side_label, league.game_key)}
                  topLeft={
                    expiry.kind === 'unavailable' ? (
                      <StatusPill variant="expire" label="Video unavailable" />
                    ) : expiry.kind === 'may_expire' ? (
                      <StatusPill variant="expire" label={expiry.text} />
                    ) : null
                  }
                  bottomRight={
                    duration ? <StatusPill variant="neutral" label={duration} className="normal-case" /> : null
                  }
                />
                <TileCaption
                  kicker={archive.match_summary?.stage_label?.trim() || null}
                  title={matchupTitle(
                    archive.match_summary?.away_side_label,
                    archive.match_summary?.home_side_label
                  )}
                  detail={                  broadcasterCaption(
                    (archive.sources?.length ? archive.sources : [archive]).map((source) => ({
                      name: broadcasterDisplayName({
                        login: source.provider_broadcaster_login,
                        twitchDisplayName: source.provider_broadcaster_display_name,
                        members,
                      }),
                      login: source.provider_broadcaster_login,
                      side: source.broadcaster_side,
                    }))
                  )}
                />
                {broadcastCount > 1 ? (
                  <StatusPill variant="neutral" label={`${broadcastCount} broadcasts`} />
                ) : null}
              </Link>
            );
          })}
        </Rail>
      ) : null}

      {upcomingMatches.length > 0 ? (
        <Rail title="Still to play this week" scrollerLabel="Games still to play">
          {upcomingMatches.map((match) => (
            <div key={match.id} className="w-72 shrink-0" data-testid="stream-upcoming-tile">
              <MatchupTile
                away={tileSide(match.away_side_label, league.game_key)}
                home={tileSide(match.home_side_label, league.game_key)}
                topLeft={<StatusPill variant="neutral" label="Not played yet" />}
              />
              <TileCaption
                title={matchupTitle(match.away_side_label, match.home_side_label)}
                detail="Goes live here when an owner streams it"
              />
            </div>
          ))}
        </Rail>
      ) : null}

      {browseTeams.length > 0 ? (
        <section data-testid="stream-browse-teams">
          <h2 className="mb-3 font-stream-display text-xl font-bold uppercase tracking-wide">Browse by team</h2>
          <div
            className="flex gap-3 overflow-x-auto overflow-y-hidden px-0.5 py-0.5"
            data-testid="stream-browse-teams-scroller"
            aria-label="Browse by team"
          >
            {browseTeams.map(({ label, visual }) => (
              <Link
                key={label}
                to={`${lscPath(`/leagues/${encodeURIComponent(leagueId)}/matches`)}${carryStreamSearch(
                  params.toString(),
                  streamViewer,
                  { team: visual.abbreviation }
                )}`}
                className="w-[88px] shrink-0"
                data-testid="stream-team-link"
                data-team-filter={visual.abbreviation}
              >
                <TeamCircle
                  name={visual.displayName}
                  abbreviation={visual.abbreviation}
                  tileColors={visual.tileColors}
                />
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <p className="text-sm text-stream-muted" data-testid="stream-home-footer">
        {STREAM_HOME_FOOTER}
      </p>
    </div>
  );
};

export default StreamLeagueHomeView;
